import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsContext = createContext(null);

const defaultSettings = {
  site_name: "رؤية",
  site_description: "شركة متكاملة تقدم حلولاً احترافية في التسويق والإنتاج والتدريب",
  logo_url: "",
  logo_dark_url: "",
  logo_light_url: "",
  phone: "+966 50 000 0000",
  email: "info@royaa.com",
  whatsapp: "966500000000",
  address: "المملكة العربية السعودية، الرياض",
  instagram: "https://instagram.com",
  twitter: "https://twitter.com",
  linkedin: "https://linkedin.com",
  youtube: "https://youtube.com",
  tiktok: "",
  snapchat: "",
  meta_title: "رؤية - التسويق والإنتاج والتدريب",
  meta_description: "شركة رائدة في تقديم الحلول الإبداعية المتكاملة",
  primary_color: "#CB360F",
  gradient_color: "#7C3AED",
  footer_text: "جميع الحقوق محفوظة",
  maintenance_mode: false,
  hero_image: "",
  marketing_image: "",
  production_image: "",
  training_image: "",
  about_image: "",
  about_team_image: "",
  recruitment_image: "",
  map_embed_url: "",
  tr_hero_badge: "التدريب والتوظيف",
  tr_hero_title: "نصنع الكفاءات",
  tr_hero_subtitle: "نطوّر المهارات ونوفر الفرص المهنية لبناء مستقبل أفضل للأفراد والمنشآت.",
  tr_training_title: "برامجنا التدريبية",
  tr_training_subtitle: "خدمات تدريبية متكاملة لرفع كفاءة الأفراد والفرق",
  tr_training_services: [
    { title: "دورات متخصصة", desc: "دورات تدريبية مكثفة في مجالات التسويق والإنتاج والمهارات الرقمية" },
    { title: "ورش عمل", desc: "ورش عمل تفاعلية عملية مع خبراء متخصصين في مختلف المجالات" },
    { title: "تطوير مهني", desc: "برامج تطوير مهني شاملة ترتقي بأداء الموظفين والفرق" },
    { title: "تدريب عملي", desc: "تدريب ميداني عملي يركز على التطبيق الفعلي للمهارات المكتسبة" },
    { title: "شهادات معتمدة", desc: "شهادات معتمدة تثبت إتمام البرامج التدريبية بنجاح" },
    { title: "برامج مخصصة", desc: "نصمم برامج تدريبية مخصصة حسب احتياجات المنشآت والأفراد" },
  ],
  tr_cta_title: "سجّل الآن في دوراتنا",
  tr_why_title: "لماذا تنضم إلينا؟",
  tr_why_features: [
    { title: "بيئة إبداعية", desc: "نوفر بيئة عمل ملهمة تحفز الإبداع والابتكار" },
    { title: "تطوير مستمر", desc: "برامج تدريبية مستمرة لتطوير مهاراتك المهنية" },
    { title: "فريق متميز", desc: "انضم لفريق من المحترفين والخبراء في مجالاتهم" },
  ],
  tr_jobs_title: "الوظائف المتاحة"
};

function hexToHSL(hex) {
  hex = hex.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function applyColorToCSS(hex) {
  try {
    const { h, s, l } = hexToHSL(hex);
    const root = document.documentElement;
    root.style.setProperty("--primary", `${h} ${s}% ${l}%`);
    root.style.setProperty("--accent", `${h} ${s}% ${l}%`);
    root.style.setProperty("--ring", `${h} ${s}% ${l}%`);
    root.style.setProperty("--chart-1", `${h} ${s}% ${l}%`);
  } catch (e) {
    console.error("Invalid color:", hex);
  }
}

function applyGradientColor(hex) {
  try {
    const { h, s, l } = hexToHSL(hex);
    document.documentElement.style.setProperty("--gradient-end", `${h} ${s}% ${l}%`);
  } catch (e) {
    console.error("Invalid gradient color:", hex);
  }
}

function getCachedSettings() {
  try {
    const cached = localStorage.getItem("site_settings");
    if (cached) {
      return { ...defaultSettings, ...JSON.parse(cached) };
    }
  } catch (e) {}
  return defaultSettings;
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(getCachedSettings);

  // Apply cached colors immediately on mount
  useEffect(() => {
    if (settings.primary_color) applyColorToCSS(settings.primary_color);
    if (settings.gradient_color) applyGradientColor(settings.gradient_color);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    axios.get(`${API}/settings`)
      .then(r => {
        const merged = { ...defaultSettings, ...r.data };
        setSettings(merged);
        localStorage.setItem("site_settings", JSON.stringify(merged));
        if (merged.primary_color) applyColorToCSS(merged.primary_color);
        if (merged.gradient_color) applyGradientColor(merged.gradient_color);
      })
      .catch(() => {});
  }, []);

  const updateSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem("site_settings", JSON.stringify(newSettings));
    if (newSettings.primary_color) applyColorToCSS(newSettings.primary_color);
    if (newSettings.gradient_color) applyGradientColor(newSettings.gradient_color);
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  return ctx?.settings || defaultSettings;
}

export function useSettingsUpdater() {
  const ctx = useContext(SettingsContext);
  return ctx?.updateSettings || (() => {});
}
